/**
 * Toast notification store
 *
 * Provides a centralized interface for displaying toast notifications
 * using the ToastManager. This store wraps the ToastManager to provide
 * React hook integration with Zustand state management.
 *
 * Features:
 * - Predefined messages for common operations
 * - Consistent styling and behavior
 * - Type-safe message access
 * - Easy integration with React components
 */

import { create } from 'zustand';

import { toastManager } from '@/lib/toast/toastManager';
import type { ToastOptions } from '@/lib/toast/config';

/**
 * Toast state interface
 *
 * Exposes both the ToastManager instance and convenience methods
 * for backward compatibility with existing code.
 */
interface ToastState {
  /** Toast manager instance with predefined messages */
  manager: typeof toastManager;
  /** Display a success toast notification */
  success: (message: string, description?: string, options?: Partial<ToastOptions>) => void;
  /** Display an error toast notification */
  error: (message: string, description?: string, options?: Partial<ToastOptions>) => void;
  /** Display a warning toast notification */
  warning: (message: string, description?: string, options?: Partial<ToastOptions>) => void;
  /** Display an info toast notification */
  info: (message: string, description?: string, options?: Partial<ToastOptions>) => void;
}

/**
 * Toast store implementation
 *
 * Creates a Zustand store that wraps the ToastManager, providing
 * both direct access to the manager and convenience methods.
 */
export const useToastStore = create<ToastState>(() => ({
  /**
   * Toast manager instance with predefined messages
   */
  manager: toastManager,

  /**
   * Display a success toast notification
   *
   * @param message Primary message text
   * @param description Optional secondary description text
   * @param options Optional toast configuration
   */
  success: (message, description, options) => {
    toastManager.success(message, description, options);
  },

  /**
   * Display an error toast notification
   *
   * @param message Primary message text
   * @param description Optional secondary description text
   * @param options Optional toast configuration
   */
  error: (message, description, options) => {
    toastManager.error(message, description, options);
  },

  /**
   * Display a warning toast notification
   *
   * @param message Primary message text
   * @param description Optional secondary description text
   * @param options Optional toast configuration
   */
  warning: (message, description, options) => {
    toastManager.warning(message, description, options);
  },

  /**
   * Display an info toast notification
   *
   * @param message Primary message text
   * @param description Optional secondary description text
   * @param options Optional toast configuration
   */
  info: (message, description, options) => {
    toastManager.info(message, description, options);
  },
}));
